import { apiClient } from './client'

export interface UserEmailAliasItem {
  id: string
  user_id: string
  email: string
  is_primary_login: boolean
  is_login_enabled: boolean
  verified_at: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface UserAuthIdentityItem {
  id: string
  user_id: string
  provider: string
  external_subject: string
  external_email: string | null
  external_login: string | null
  is_active: boolean
  verified_at: string | null
  linked_at: string
  linked_by: string | null
  last_seen_at: string | null
  created_at: string
  updated_at: string
}

export interface UserLoginReadinessItem {
  user_id: string
  canonical_email: string
  email_domain: string | null
  zitadel_id: string | null
  expected_login_mode: string
  readiness_status: string
  readiness_label: string
  readiness_note: string
  yandex_idp_id: string | null
  yandex_expected: boolean
  yandex_linked: boolean
  yandex_login: string | null
  auth_method_types: string[]
  has_password_auth: boolean
  control_plane_alias_count: number
  control_plane_identity_count: number
  warnings: string[]
  api_error: string | null
}

export interface UserYandexLinkOperationItem {
  user_id: string
  canonical_email: string
  zitadel_id: string | null
  yandex_idp_id: string | null
  yandex_expected: boolean
  proposed_yandex_login: string | null
  current_yandex_logins: string[]
  current_auth_method_types: string[]
  status: string
  status_label: string
  status_note: string
  can_apply: boolean
  can_unlink: boolean
  would_change: boolean
  warnings: string[]
  blocking_issues: Array<Record<string, unknown>>
  api_error: string | null
}

export interface UserEmailAliasCreatePayload {
  email: string
  is_primary_login?: boolean
  is_login_enabled?: boolean
  is_verified?: boolean
}

export interface UserEmailAliasUpdatePayload {
  is_primary_login?: boolean
  is_login_enabled?: boolean
  is_verified?: boolean
}

export interface UserAuthIdentityCreatePayload {
  provider: string
  external_subject: string
  external_email?: string | null
  external_login?: string | null
  is_active?: boolean
  is_verified?: boolean
}

export interface UserAuthIdentityUpdatePayload {
  external_email?: string | null
  external_login?: string | null
  is_active?: boolean
  is_verified?: boolean
}

export interface UserYandexLinkPreviewPayload {
  external_login?: string | null
  allow_existing_auth_methods?: boolean
}

export interface UserYandexLinkUnlinkPayload {
  external_login?: string | null
}

export const fetchUserEmailAliases = async (userId: string): Promise<UserEmailAliasItem[]> => {
  const response = await apiClient.get<UserEmailAliasItem[]>(`/users/${userId}/email-aliases`)
  return response.data
}

export const createUserEmailAlias = async (
  userId: string,
  data: UserEmailAliasCreatePayload
): Promise<UserEmailAliasItem> => {
  const response = await apiClient.post<UserEmailAliasItem>(`/users/${userId}/email-aliases`, data)
  return response.data
}

export const updateUserEmailAlias = async (
  userId: string,
  aliasId: string,
  data: UserEmailAliasUpdatePayload
): Promise<UserEmailAliasItem> => {
  const response = await apiClient.patch<UserEmailAliasItem>(`/users/${userId}/email-aliases/${aliasId}`, data)
  return response.data
}

export const deleteUserEmailAlias = async (userId: string, aliasId: string): Promise<void> => {
  await apiClient.delete(`/users/${userId}/email-aliases/${aliasId}`)
}

export const fetchUserAuthIdentities = async (userId: string): Promise<UserAuthIdentityItem[]> => {
  const response = await apiClient.get<UserAuthIdentityItem[]>(`/users/${userId}/auth-identities`)
  return response.data
}

export const fetchUserLoginReadiness = async (userId: string): Promise<UserLoginReadinessItem> => {
  const response = await apiClient.get<UserLoginReadinessItem>(`/users/${userId}/login-readiness`)
  return response.data
}

export const createUserAuthIdentity = async (
  userId: string,
  data: UserAuthIdentityCreatePayload
): Promise<UserAuthIdentityItem> => {
  const response = await apiClient.post<UserAuthIdentityItem>(`/users/${userId}/auth-identities`, data)
  return response.data
}

export const updateUserAuthIdentity = async (
  userId: string,
  identityId: string,
  data: UserAuthIdentityUpdatePayload
): Promise<UserAuthIdentityItem> => {
  const response = await apiClient.patch<UserAuthIdentityItem>(`/users/${userId}/auth-identities/${identityId}`, data)
  return response.data
}

export const deleteUserAuthIdentity = async (userId: string, identityId: string): Promise<void> => {
  await apiClient.delete(`/users/${userId}/auth-identities/${identityId}`)
}

export const previewUserYandexLink = async (
  userId: string,
  data: UserYandexLinkPreviewPayload
): Promise<UserYandexLinkOperationItem> => {
  const response = await apiClient.post<UserYandexLinkOperationItem>(`/users/${userId}/yandex-link/preview`, data)
  return response.data
}

export const applyUserYandexLink = async (
  userId: string,
  data: UserYandexLinkPreviewPayload
): Promise<UserYandexLinkOperationItem> => {
  const response = await apiClient.post<UserYandexLinkOperationItem>(`/users/${userId}/yandex-link/apply`, data)
  return response.data
}

export const unlinkUserYandexLink = async (
  userId: string,
  data: UserYandexLinkUnlinkPayload
): Promise<UserYandexLinkOperationItem> => {
  const response = await apiClient.post<UserYandexLinkOperationItem>(`/users/${userId}/yandex-link/unlink`, data)
  return response.data
}
